import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { buildPreviewShellHtml, buildArticleBody, type PreviewData } from "./preview-html-builder";

interface SharePreviewIframeProps {
  previewData: PreviewData;
  className?: string;
}

export function SharePreviewIframe({ previewData, className = "" }: SharePreviewIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const bodyHtml = buildArticleBody(previewData);
  const debouncedHtml = useDebounce(bodyHtml, 100);

  const shellHtml = useMemo(() => buildPreviewShellHtml(), []);

  useEffect(() => {
    if (!isLoaded) return;

    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc?.body) return;

    doc.body.innerHTML = debouncedHtml;
  }, [debouncedHtml, isLoaded]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className={`relative h-full w-full ${className}`}>
      {!isLoaded && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/50">
          <div className="flex flex-col items-center gap-2">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-muted-foreground text-xs">미리보기 로딩 중...</span>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        srcDoc={shellHtml}
        title="아티클 미리보기"
        sandbox="allow-same-origin"
        onLoad={handleLoad}
        className="h-full w-full border-0"
      />
    </div>
  );
}
